import {useEffect, useRef} from 'react';

import styled from 'styled-components';

import {ICoach} from '@stryd/models';
import {colors, dimensions} from '@stryd/react-ui';

import {withBasePath} from 'src/utils/router';

let backgroundId = -1;
const getPortraitId = () => `bg-img-${++backgroundId}`;
const lazyClass = 'lazy';
const coachContainerPadding = '15px';

const CoachesProfile = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  &:hover {
    cursor: pointer;
  }
`;

type PortraitContainerProps = {
  imgURL: string;
};

const PortraitContainer = styled.div<PortraitContainerProps>`
  position: relative;
  background-image: url(${(props) => props.imgURL});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  padding: 50% 0;
  &.lazy {
    background-image: none;
  }
`;

const DarkOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
`;

//add d0 for opacity
const SelectedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    ${colors.themes.dark.infoBtnBackground}d0 0%,
    rgba(0, 0, 0, 0.1) 100%
  );
  box-shadow: 0px 0px 0px 2px ${colors.themes.dark.infoBtnBackground} inset;
`;

const Info = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: ${coachContainerPadding};
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 1.2em;
  text-transform: capitalize;
  color: ${colors.themes.dark.buttonPrimaryText};
`;

const Name = styled.h3`
  display: flex;
  text-transform: uppercase;
  color: ${colors.themes.dark.buttonPrimaryText};
  font-size: 20px;
  margin-bottom: 5px;
`;

const DataEntry = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  font-size: 15px;
`;

const LanguageIcon = styled.img`
  margin-right: 5px;
  height: 20px;
`;

const Flags = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  padding: ${coachContainerPadding};
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
`;

const FlagImg = styled.img`
  display: flex;
  width: 25px;
  margin-left: 10px;
  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    width: 30px;
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    width: 35px;
  }
`;

const DataValue = styled.span`
  color: ${colors.themes.dark.buttonPrimaryText};
`;

type CoachesEntryProps = {
  handleClick: (coach: ICoach) => void;
  coach: ICoach;
  selected: boolean;
};

const CoachesEntry: React.FC<React.PropsWithChildren<CoachesEntryProps>> = ({
  handleClick,
  coach,
  selected,
}) => {
  const portraitRef = useRef(null);
  const {current: portraitId} = useRef(getPortraitId());

  useEffect(() => {
    const imageContainer = portraitRef.current;

    if (!imageContainer) return;

    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const image = entry.target;
          image.classList.remove(lazyClass);
          imageObserver.unobserve(image);
        }
      });
    });

    imageObserver.observe(imageContainer);
  }, []);

  return (
    <CoachesProfile
      onClick={() => {
        handleClick(coach);
      }}
    >
      <PortraitContainer
        imgURL={coach.thumbnail}
        id={portraitId}
        ref={portraitRef}
        className={lazyClass}
      >
        <DarkOverlay />
        {selected && <SelectedOverlay />}
      </PortraitContainer>
      <Flags>
        <FlagImg
          src={withBasePath(`/icons/flags/${coach.country?.toLowerCase()}.svg`)}
        />
      </Flags>
      <Info>
        <Name>{`${coach.prefix} ${coach.first_name}`}</Name>
        {coach.middle_name.length > 0 && <Name>{coach.middle_name}</Name>}
        <Name>{coach.last_name}</Name>
        <DataEntry>
          <LanguageIcon src={withBasePath('/icons/icon-language.svg')} />
          <DataValue>{coach.languages.join(', ')}</DataValue>
        </DataEntry>
      </Info>
    </CoachesProfile>
  );
};

export default CoachesEntry;
